import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Input, Text, TextInput, Button } from "@mantine/core";
import { shallow } from "zustand/shallow";
import { useTranslation } from "react-i18next";
import InputMask from "react-input-mask";
import styled from "styled-components";

import { CodeInput } from "@components/Auth";
import ViewTitle from "./ViewTitle";
import useAppStore from "./microsite-store";
import EffortRegistered from "./EffortRegistered";
import { UserLockModal } from "@components/User";
import { micrositeUserLookup } from "./userLookup";
import { rideChallengeUserLookup } from "@components/RideChallengeApp/userLookup";
import RideChallengeHeader from "@components/RideChallengeApp/RideChallengeHeader";
import { useAssociateExternalUserData } from "@components/Microsite/useAssociateExternalUserData";

import { OtherButton } from "./UserLocation";

export default function UserCheck() {
  const [method, setMethod] = useState("phone");
  const [formValues, setFormValues] = useState({
    email: "",
    mobile_phone: "",
    code: "",
  });
  const [remoteCode, setRemoteCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [registrationInfo, setRegistrationInfo] = useState(null);
  const [lockData, setLockData] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const { associateFromSearchParams } = useAssociateExternalUserData();

  const { t } = useTranslation();

  const { setVerificationMethod, entityInfo } = useAppStore(
    (state) => ({
      setVerificationMethod: state.setVerificationMethod,
      entityInfo: state.entityInfo,
    }),
    shallow
  );

  const isRideChallenge = entityInfo.is_ride_challenge;
  const isVehicleDataEffort = entityInfo.is_vehicle_data_effort;

  useEffect(() => {
    if (formValues.code.length === 6 && !verifying) {
      verifyCode();
    }
  }, [formValues.code]);

  function onSubmit() {
    const digitsOnly = formValues.mobile_phone.replace(/\D/g, "");
    if (method === "phone" && digitsOnly.length !== 10) return;

    setLoading(true);

    if (method === "email")
      localStorage.setItem("mixerEntryEmail", formValues.email);

    // Get current store state for request
    const { campaignId, entityInfo, location, coords } = useAppStore.getState();

    const req = {};

    // Location handling
    const locationId = entityInfo?.location_id || location?.id;
    if (locationId) req.location_id = locationId;

    // Campaign/Effort info
    if (entityInfo.campaign_effort_id)
      req.campaign_effort_id = entityInfo.campaign_effort_id;
    if (campaignId) req.campaign_id = campaignId;

    // Coordinates
    if (coords) {
      req.latitude = coords.latitude;
      req.longitude = coords.longitude;
    }

    if (method === "email") req.email = formValues.email;
    if (method === "phone") req.mobile_phone = formValues.mobile_phone;

    axios
      .post(`/microsite/send-verification/`, req)
      .then(({ data }) => {
        setLoading(false);
        setRemoteCode(data.response[0]);
      })
      .catch((err) => {
        toast.error(err);
        setLoading(false);
      });
  }

  function verifyCode() {
    const req = {
      code: formValues.code,
      remote_code_id: remoteCode.remote_code_id,
    };

    setVerifying(true);

    axios
      .post(`/microsite/verify-code/`, req)
      .then(({ data }) => {
        setVerificationMethod(method === "email" ? "email" : "mobile_phone");
        associateFromSearchParams({
          additionalPayload: {
            email: formValues.email,
            mobile_phone: formValues.mobile_phone,
          },
        });
        userLookup();
      })
      .catch((err) => {
        setFormValues({
          ...formValues,
          code: "",
        });
        toast.error(err);
        setVerifying(false);
      });
  }

  function userLookup() {
    const userInfo = {
      email: method === "email" ? formValues.email : undefined,
      mobile_phone: method === "phone" ? formValues.mobile_phone : undefined,
    };

    if (isRideChallenge || isVehicleDataEffort) {
      rideChallengeUserLookup({
        userInfo,
        onModal: (user) => setLockData(user),
      }).catch((err) => {
        setLoading(false);
      });
    } else {
      micrositeUserLookup({
        userInfo,
        onModal: (user) => setLockData(user),
        onRegistration: (user) => setRegistrationInfo(user),
      })
        .then((usr) => {
          true;
        })
        .catch((err) => {
          setLoading(false);
        });
    }
  }

  const validEmail = formValues.email.match(/^\S+@\S+\.\S+$/) ? true : false;
  const showHeader = isRideChallenge || isVehicleDataEffort;

  if (registrationInfo) {
    return (
      <Wrapper showHeader={showHeader}>
        <EffortRegistered
          registrationInfo={registrationInfo}
          info={entityInfo}
        />
      </Wrapper>
    );
  }

  if (remoteCode) {
    return (
      <Wrapper showHeader={showHeader}>
        {/* <UserLockModal data={lockData} /> */}
        {remoteCode.opted_out ? (
          <>
            <ViewTitle
              title={t("generic_error_title")}
              subtitle={`${formValues.mobile_phone} Opt-Out`}
            />
            <Text align="center" mt="lg" c="white" size="lg">
              {t("phone_code_opted_out")}
            </Text>
            <Text align="center" mt="lg" c="white" size="lg">
              {t("phone_code_opted_out_subtitle")}
            </Text>
            <Text align="center" c="white" size="lg" fw={700}>
              {remoteCode.opt_in_number}
            </Text>
            <OtherButton
              onClick={() => {
                setRemoteCode(null);
                setFormValues({
                  ...formValues,
                  code: "",
                });
              }}
              text={t("phone_code_try_again")}
            />
          </>
        ) : (
          <>
            <ViewTitle
              title={t("phone_code_title")}
              subtitle={`${t("phone_code_subtitle")} ${
                method === "email" ? formValues.email : formValues.mobile_phone
              }`}
            />
            <CodeInput
              value={formValues.code}
              onChange={(e) =>
                setFormValues({
                  ...formValues,
                  code: e,
                })
              }
            />
            <OtherButton
              onClick={() => {
                setRemoteCode(null);
                setVerifying(false);
                setFormValues({
                  ...formValues,
                  code: "",
                });
              }}
              text={t("back")}
            />
          </>
        )}
      </Wrapper>
    );
  }

  return (
    <Wrapper showHeader={showHeader}>
      <ViewTitle
        title={t(method === "email" ? "email_question" : "phone_question")}
      />
      {method === "phone" && (
        <React.Fragment>
          <StyledPhone>
            <Input.Wrapper>
              <Input
                component={InputMask}
                placeholder="(XXX) XXX-XXXX"
                mask="(999) 999-9999"
                type="tel"
                onChange={(e) => {
                  setFormValues({
                    ...formValues,
                    mobile_phone: e.target.value,
                  });
                }}
                value={formValues.mobile_phone}
              />
            </Input.Wrapper>
          </StyledPhone>
        </React.Fragment>
      )}
      {method === "email" && (
        <StyledEmail>
          <TextInput
            value={formValues.email}
            onChange={(e) =>
              setFormValues({
                ...formValues,
                email: e.target.value,
              })
            }
            placeholder="email"
            size="lg"
            type="email"
          />
        </StyledEmail>
      )}
      <Button
        fullWidth
        mt="sm"
        size="lg"
        onClick={onSubmit}
        disabled={
          method === "email"
            ? !formValues.email || !validEmail
            : !formValues.mobile_phone
        }
        loading={loading}
      >
        {t(method === "email" ? "email_button" : "phone_button")}
      </Button>
      {method === "phone" && (
        <Text align="center" mt="lg" c="white" size="sm">
          {t("phone_disclosure")}
        </Text>
      )}
      <div
        style={{
          marginTop: "2em",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Button
          onClick={() => setMethod(method === "email" ? "phone" : "email")}
          size="xs"
          radius="lg"
        >
          {t("auth_method_toggle_button", {
            method: method === "email" ? t("phone") : "email",
          })}
        </Button>
      </div>
    </Wrapper>
  );
}

const StyledPhone = styled.div`
  input {
    height: 50px;
    line-height: 48px;
    padding: 20px;
    font-size: 18px;
    text-align: center;
  }
`;
const StyledEmail = styled.div`
  input {
    text-align: center;
    background-color: var(--mantine-color-gray-2);
  }
`;

const Wrapper = ({ children, showHeader = false }) => {
  return (
    <div>
      {showHeader && <RideChallengeHeader />}
      <div>{children}</div>
    </div>
  );
};
